/**
 * Reveals elements as they scroll into view.
 *
 * Replaces scrollreveal 3.4, which is unmaintained and was pulled in from a deep
 * source path (`scrollreveal/src/scrollreveal.js`) to dodge its own build. The
 * effect it provided is a fade, rise and slight scale on first intersection,
 * which IntersectionObserver plus a CSS transition does natively.
 */

const REVEAL_SELECTOR = '.rev-item';

export interface RevealOptions {
  /** Fraction of the element that must be visible before revealing. */
  threshold?: number;
  /** Selector for the elements to reveal. */
  selector?: string;
}

export function initReveal(options: RevealOptions = {}): void {
  const { threshold = 0.3, selector = REVEAL_SELECTOR } = options;

  const targets = Array.from(document.querySelectorAll<HTMLElement>(selector));

  if (targets.length === 0) {
    return;
  }

  // Anyone who has asked not to see motion gets the end state immediately.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  targets.forEach((el) => el.classList.add('will-reveal'));

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }

        entry.target.classList.add('is-revealed');
        // Revealing is one-way, so stop watching once it has happened.
        observer.unobserve(entry.target);
      }
    },
    { threshold },
  );

  targets.forEach((el) => observer.observe(el));
}
